import {
  IUser,
  IUserMiniWithRole,
  TEffectiveRole,
  TNvRoleTypeSchool,
  TNvRoleTypeShelter,
  TSchoolDelegatedRole,
  TShelterDelegatedRole,
} from '../../../typings/interfaces/user.interface';
import { IM_USER_CONSTANTS } from './../im-user';
import { userCate } from './im-user.helper';
import { TValidPartnerTypes } from '../../../typings/interfaces/partner.interface';

export interface IRoleTypesMapper {
  [partnerType: string]: TEffectiveRole;
}

export class ImUserCore {
  getRoleTypes(user: IUser | IUserMiniWithRole): IRoleTypesMapper[] {
    const roles = IM_USER_CONSTANTS.PARTNER_TYPES.reduce((_roles, partnerType: TValidPartnerTypes) => {
      const role = this.getRoleType(user, partnerType);
      if (role) {
        _roles.push({ [partnerType]: role });
      }
      return _roles;
    }, []);
    return roles;
  }

  getRoleType(user: IUser | IUserMiniWithRole, partnerType: TValidPartnerTypes = 'school'): TEffectiveRole {
    const cated = userCate(user, partnerType);
    return cated({
      school: this.getRoleTypeSchool,
      shelter: this.getRoleTypeShelter,
    });
  }

  private getRoleTypeSchool(user: IUser | IUserMiniWithRole): TEffectiveRole {
    return user.nvRole ? user.delegatedRole || user.nvRole.type : null;
  }

  private getRoleTypeShelter(user: IUser | IUserMiniWithRole): TEffectiveRole {
    return user.nvRoleShelter ? user.delegatedRoleShelter || user.nvRoleShelter.type : null;
  }

  hasPartnerId(user: IUser, partnerId: string, partnerType: TValidPartnerTypes = 'school'): boolean {
    const cated = userCate(user, partnerType);
    return cated({
      school: this.hasSchoolId.bind(this, user, partnerId),
      shelter: this.hasShelterId.bind(this, user, partnerId),
    });
  }

  private hasSchoolId(user: IUser, schoolId: string): boolean {
    const {
      nvRole: { schoolId: _schoolId },
      _role_clusterSchoolIds,
    } = user;
    if (_schoolId) {
      return schoolId === _schoolId;
    } else if (_role_clusterSchoolIds) {
      return _role_clusterSchoolIds.includes(schoolId);
    }
    return false;
  }

  private hasShelterId(user: IUser, shelterId: string): boolean {
    const {
      nvRoleShelter: { shelterId: _shelterId },
      _role_shelterClusterShelterIds,
    } = user;
    if (_shelterId) {
      return shelterId === _shelterId;
    } else if (_role_shelterClusterShelterIds) {
      return _role_shelterClusterShelterIds.includes(shelterId);
    }
    return false;
  }

  getRoleTypeOnAllLevels(user: IUser, partnerType: TValidPartnerTypes = 'school'): TEffectiveRole {
    return this.getRoleTypeOnPartnerLevel(user, partnerType) || this.getRoleType(user, partnerType);
  }

  getRoleTypeOnPartnerLevel(user: IUser, partnerType: TValidPartnerTypes = 'school'): TEffectiveRole {
    const cated = userCate(user, partnerType);
    return cated({
      school: user => user.effectiveSchoolRoleType,
      shelter: user => user.effectiveShelterRoleType,
    });
  }

  recalcRoleTypeOnPartnerLevel({ user, partnerType, stateOrUrl }): TEffectiveRole {
    const userRole = this.getRoleType(user, partnerType);
    const cated = userCate(user, partnerType);
    const recalcedRole = cated({
      school: user => {
        if (stateOrUrl.name.includes('main.school')) {
          const userRoleOnSchoolLvl = this.getRoleTypeOnPartnerLevel(user, 'school');
          // TODO-JCHU: replace the above method with the method below, and refactor getRoleTypeOnPartnerLevel
          // const userRoleOnSchoolLvl = this.recalcRoleTypeOnSchoolLevel(user, schoolId);
          return userRoleOnSchoolLvl || userRole;
        } else {
          return userRole;
        }
      },
      shelter: user => {
        // TODO- needs to update shelter id regex once shelter id format is confirmed
        const matchedShelterRoute = stateOrUrl.match(/\/shelter\/.*\//gi);
        const matchedShelterId = matchedShelterRoute && matchedShelterRoute[0].split('/')[2];
        const shelterNetworkSettingsRoute = matchedShelterId === 'settings';
        if (!!matchedShelterId && !shelterNetworkSettingsRoute) {
          const userRoleOnShelterLvl = this.recalcRoleTypeOnShelterLevel(user, matchedShelterId);
          return userRoleOnShelterLvl || userRole;
        } else {
          return userRole;
        }
      },
    });
    return recalcedRole;
  }

  // this method is to check school cluster roles
  // can be used on school cluster user and hybrid user that is school cluster user on school side
  // returns effectiveSchoolRoleType given a school id
  // this method gets called in main.route, guarantees that user.effectiveSchoolRoleType is always populated in user obj
  // TODO-JCHU: make this method private and only expose the parent method to main.route
  recalcRoleTypeOnSchoolLevel(user: IUser, schoolId: string): TSchoolDelegatedRole | TNvRoleTypeSchool {
    let effectiveSchoolRoleType;
    const role = this.getRoleType(user, 'school');
    if (role === 'network_and_school' && schoolId) {
      const { edit_all, view_all, cluster_edit_all, cluster_view_all } = user.portfolio.permissions;
      if (cluster_edit_all === true || (edit_all && edit_all.includes(schoolId))) {
        effectiveSchoolRoleType = 'school_admin';
      } else if (cluster_view_all === true || (view_all && view_all.includes(schoolId))) {
        effectiveSchoolRoleType = 'view_all';
      } else {
        effectiveSchoolRoleType = 'school';
      }
    }
    return effectiveSchoolRoleType;
  }

  // this method is to check shelter roles against context shelter Id
  // can be used on all types of shelter roles
  // returns effectiveShelterRoleType given a shelter id
  // this method gets called in route guard through parent ImUser.recalcRoleTypeOnPartnerLevel
  private recalcRoleTypeOnShelterLevel(user: IUser, shelterId: string): TShelterDelegatedRole | TNvRoleTypeShelter {
    let effectiveShelterRoleType;
    const role = this.getRoleType(user, 'shelter');
    if (shelterId) {
      // shelter cluster admin
      if (role === 'shelter_cluster_admin') {
        if (this.hasShelterId(user, shelterId)) {
          effectiveShelterRoleType = role;
        } else {
          effectiveShelterRoleType = 'shelter';
        }
      }
      // shelter cluster non-admin
      if (role === 'shelter_only') {
        if (this.hasShelterId(user, shelterId)) {
          effectiveShelterRoleType = 'shelter_view_all';
        } else {
          effectiveShelterRoleType = 'shelter';
        }
      }
      // single shelter user
      if (role === 'shelter_view_all') {
        if (this.hasShelterId(user, shelterId)) {
          effectiveShelterRoleType = role;
        } else {
          effectiveShelterRoleType = 'shelter';
        }
      }
    }
    return effectiveShelterRoleType;
  }
}
